/** Handles damage calculation, hit application, backstab detection, hitstop, and screen shake. */
import Phaser from 'phaser';
import { ConfigLoader } from './ConfigLoader';
import { HitstopSystem } from './HitstopSystem';
import { ScreenEffects } from './ScreenEffects';
import { Entity } from '../entities/Entity';
import { HealthComponent } from '../components/HealthComponent';
import { AIComponent } from '../components/AIComponent';
import type { AttackType } from '../components/CombatComponent';
import type { UpgradeEffectResolver } from './UpgradeEffectResolver';
import type { Position } from '../utils/Types';
import { dot, degreesToRadians } from '../utils/Math';

export interface HitResult {
  target: Entity;
  attacker: Entity;
  damage: number;
  baseDamage: number;
  isCrit: boolean;
  isBackstab: boolean;
  attackType: AttackType;
  direction: Position;
}

/** Orchestrates hit resolution — damage, crits, backstabs, hitstop, and screen shake. */
export class CombatSystem {
  private scene: Phaser.Scene;
  private hitstop: HitstopSystem;
  private screenEffects: ScreenEffects;

  constructor(scene: Phaser.Scene, hitstop: HitstopSystem, screenEffects: ScreenEffects) {
    this.scene = scene;
    this.hitstop = hitstop;
    this.screenEffects = screenEffects;
  }

  /** Compute raw damage and crit flag for an attack, factoring in skill level. */
  calculateDamage(attackType: AttackType, skillLevel: number = 0): { damage: number; baseDamage: number; isCrit: boolean } {
    const config = ConfigLoader.getDamageConfig(attackType);
    const baseDamage = config.baseDamage;
    let damage = baseDamage;

    // Crit calculation
    const critChance = config.critChanceBase + config.critChancePerSkillLevel * skillLevel;
    const isCrit = Math.random() < critChance;
    if (isCrit) {
      damage = Math.round(damage * config.critMultiplier);
    }

    damage = Math.max(config.minimumDamage, damage);
    return { damage, baseDamage, isCrit };
  }

  /** Apply a hit from attacker to target, running backstab and hitstop logic; returns null if target is dead. */
  applyHit(attacker: Entity, target: Entity, attackType: AttackType, direction: Position, options?: { godMode?: boolean; damageMultiplier?: number; stealthAttack?: boolean; upgradeResolver?: UpgradeEffectResolver }): HitResult | null {
    const targetHealth = target.getComponent<HealthComponent>('health');
    if (!targetHealth || targetHealth.isDead) return null;

    let { damage, baseDamage, isCrit } = this.calculateDamage(attackType);

    // Backstab check: if target has a facing direction, check if attack comes from behind
    let isBackstab = false;
    const targetAI = target.getComponent<AIComponent>('ai');
    if (targetAI) {
      const damageConfig = ConfigLoader.getDamageConfig(attackType);
      let backstabAngle = damageConfig.backstabAngle;
      let backstabMultiplier = damageConfig.backstabMultiplier;

      // Stealth backstab bonuses: wider arc + stronger multiplier from passives
      if (options?.stealthAttack && options.upgradeResolver) {
        backstabAngle += options.upgradeResolver.getStealthBackstabAngleBonus();
        const stealthMult = options.upgradeResolver.getStealthBackstabMultiplier();
        if (stealthMult !== null) backstabMultiplier = stealthMult;
      }

      // Dot product: attack direction vs target facing. Positive = same direction = from behind
      const dotProduct = dot(direction.x, direction.y, targetAI.facingX, targetAI.facingY);
      const backstabCos = Math.cos(degreesToRadians(backstabAngle / 2));
      if (dotProduct > backstabCos) {
        isBackstab = true;
        damage = Math.round(damage * backstabMultiplier);
      }
    }

    // Block reduction (e.g. frontal shield block)
    if (options?.damageMultiplier !== undefined) {
      damage = Math.round(damage * options.damageMultiplier);
      damage = Math.max(ConfigLoader.getDamageConfig(attackType).minimumDamage, damage);
    }

    // Apply damage (skip if god mode — damage numbers still show)
    if (!options?.godMode) {
      targetHealth.takeDamage(damage);
    }
    const targetDied = targetHealth.isDead;

    // Hitstop: freeze attacker always, skip target if they just died
    // (dying target gets death frame immediately — hitstop white tint would override it)
    const hitstopFrames = ConfigLoader.getHitstopFrames(attackType);
    if (hitstopFrames !== undefined) {
      this.hitstop.freeze(attacker, hitstopFrames);
      if (!targetDied) this.hitstop.freeze(target, hitstopFrames);
    }

    // Screen shake on hit
    this.screenEffects.heavyHitEffect();

    return { target, attacker, damage, baseDamage, isCrit, isBackstab, attackType, direction };
  }

  /** Apply a projectile hit to a target — no backstab check, no attacker entity freeze. */
  applyProjectileHit(target: Entity, baseDamage: number, attackType: AttackType, direction: Position, options?: { godMode?: boolean; damageMultiplier?: number }): Omit<HitResult, 'attacker' | 'isBackstab'> | null {
    const targetHealth = target.getComponent<HealthComponent>('health');
    if (!targetHealth || targetHealth.isDead) return null;

    const config = ConfigLoader.getDamageConfig(attackType);
    const isCrit = Math.random() < config.critChanceBase;
    let damage = baseDamage;
    if (isCrit) {
      damage = Math.round(damage * config.critMultiplier);
    }
    damage = Math.max(config.minimumDamage, damage);

    // Apply damage multiplier (e.g. player DR)
    if (options?.damageMultiplier !== undefined) {
      damage = Math.round(damage * options.damageMultiplier);
      damage = Math.max(config.minimumDamage, damage);
    }

    if (!options?.godMode) {
      targetHealth.takeDamage(damage);
    }
    const targetDied = targetHealth.isDead;

    // Hitstop on target only — skip if they just died (death frame takes priority)
    const hitstopFrames = ConfigLoader.getHitstopFrames(attackType);
    if (hitstopFrames !== undefined && !targetDied) {
      this.hitstop.freeze(target, hitstopFrames);
    }

    this.screenEffects.heavyHitEffect();

    return { target, damage, baseDamage, isCrit, attackType, direction };
  }

  /** Tick the hitstop system forward one frame. */
  update(): void {
    this.hitstop.update();
  }
}
